import { AlertTriangle, Eye, Loader2, Sparkles, X } from 'lucide-react';

import { getProxiedImageUrl } from '@/lib/image-proxy';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePreview } from '@/api/publishing';
import { useTagSuggestions } from '@/api/tag-suggestions';
import { LocationPreviewCard } from './LocationPreviewCard';
import { CURRENCY_NOT_SET_LABEL } from '@/lib/format-money';
import type { PreviewWarning } from '@/types/api';

interface LocationPreviewDialogProps {
  syncedLocationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function WarningAlert({ warning }: { warning: PreviewWarning }): JSX.Element {
  const isNoImages = warning.type === 'no_images';

  return (
    <div
      className={`flex items-start gap-2 rounded-md border p-3 ${
        isNoImages
          ? 'border-amber-200 bg-amber-50 text-amber-800'
          : 'border-blue-200 bg-blue-50 text-blue-800'
      }`}
      data-testid={`preview-warning-${warning.type}`}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <span className="text-sm">{warning.message}</span>
    </div>
  );
}

export function LocationPreviewDialog({
  syncedLocationId,
  open,
  onOpenChange,
}: LocationPreviewDialogProps): JSX.Element {
  const { data, isLoading, error, refetch } = usePreview(syncedLocationId, open);
  const { data: tagSuggestions } = useTagSuggestions(syncedLocationId);

  // Build a map of tagId -> tagName from suggestions
  const tagNameMap = new Map<number, string>();
  if (tagSuggestions?.suggestions.location) {
    for (const tag of tagSuggestions.suggestions.location) {
      tagNameMap.set(tag.tagId, tag.tagName);
    }
  }
  if (tagSuggestions?.suggestions.images) {
    for (const imageTags of Object.values(tagSuggestions.suggestions.images)) {
      for (const tag of imageTags) {
        tagNameMap.set(tag.tagId, tag.tagName);
      }
    }
  }

  // Helper to get tag names for an image index
  const getImageTagNames = (imageIndex: number): string[] => {
    const tagIds = data?.success ? (data.preview.confirmedImageTags?.[String(imageIndex)] ?? []) : [];
    return tagIds
      .map((id) => tagNameMap.get(id))
      .filter((name): name is string => name !== undefined);
  };

  const handleRetry = () => {
    void refetch();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="location-preview-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            <DialogTitle>Location Preview</DialogTitle>
          </div>
          <DialogDescription>
            Preview how this location will appear on ScoutAround
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Loading state */}
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">Loading preview...</p>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-6 text-center">
              <X className="mx-auto h-8 w-8 text-destructive" />
              <p className="mt-2 font-medium text-destructive">Failed to load preview</p>
              <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={handleRetry}>
                Try Again
              </Button>
            </div>
          )}

          {/* Preview response */}
          {data && (
            <>
              {/* Error response from API */}
              {!data.success && (
                <div className="space-y-4">
                  <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                    <p className="font-medium text-destructive">{data.error}</p>
                    {'validationErrors' in data && data.validationErrors && data.validationErrors.length > 0 && (
                      <ul className="mt-2 space-y-1 text-sm">
                        {data.validationErrors.map((err, index) => (
                          <li
                            key={index}
                            className={`flex items-center gap-2 ${
                              err.overridable ? 'text-amber-600' : 'text-destructive'
                            }`}
                          >
                            <span className="font-mono text-xs">{err.field}:</span>
                            <span>{err.message}</span>
                            {err.overridable && (
                              <span className="text-xs opacity-75">(overridable)</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {'isForcePublishable' in data && data.isForcePublishable && (
                      <p className="mt-3 text-sm text-amber-600 font-medium" data-testid="preview-force-publishable-note">
                        This location can be force-published. Use the Publish button to proceed.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Success response */}
              {data.success && (
                <div className="space-y-4">
                  {/* Warnings */}
                  {data.warnings.length > 0 && (
                    <div className="space-y-2">
                      {data.warnings.map((warning, index) => (
                        <WarningAlert key={index} warning={warning} />
                      ))}
                    </div>
                  )}

                  {/* Preview card */}
                  <LocationPreviewCard preview={data.preview} />

                  {/* Overridden errors (shown when force-published preview) */}
                  {(data.overriddenErrors?.length ?? 0) > 0 && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="preview-overridden-errors">
                      <p className="text-sm font-medium text-amber-800">
                        The following fields were overridden:
                      </p>
                      <ul className="mt-1 space-y-1 text-sm text-amber-700">
                        {(data.overriddenErrors ?? []).map((err, index) => (
                          <li key={index}>{err.message}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Publishable status */}
                  <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Publication Status:</span>
                      {data.isPublishable ? (
                        <Badge variant="success">Ready to Publish</Badge>
                      ) : (
                        <Badge variant="destructive">Not Ready</Badge>
                      )}
                    </div>
                    {data.preview.partnerType && (
                      <div className="text-xs text-muted-foreground">
                        Source: {data.preview.partnerAccountName} ({data.preview.partnerType})
                      </div>
                    )}
                  </div>

                  {/* Additional details */}
                  <details className="group">
                    <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                      View full details
                    </summary>
                    <div className="mt-3 space-y-3 rounded-lg border bg-muted/20 p-4 text-sm">
                      {/* Description */}
                      <div>
                        <h4 className="font-medium">Full Description</h4>
                        <p className="mt-1 whitespace-pre-wrap text-muted-foreground">
                          {data.preview.description?.en ?? 'No description provided'}
                        </p>
                      </div>

                      {/* Address */}
                      <div>
                        <h4 className="font-medium">Full Address</h4>
                        <p className="mt-1 text-muted-foreground">
                          {[
                            data.preview.address?.street,
                            data.preview.address?.city,
                            data.preview.address?.state,
                            data.preview.address?.postalCode,
                            data.preview.address?.country,
                          ]
                            .filter(Boolean)
                            .join(', ') || 'No address provided'}
                        </p>
                        {data.preview.address?.latitude && data.preview.address.longitude && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Coordinates: {data.preview.address.latitude}, {data.preview.address.longitude}
                          </p>
                        )}
                      </div>

                      {/* All features */}
                      {(data.preview.features?.length ?? 0) > 0 && (
                        <div>
                          <h4 className="font-medium">All Features</h4>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {data.preview.features?.map((feature) => (
                              <Badge key={feature} variant="outline" className="text-xs">
                                {feature}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* All amenities */}
                      {(data.preview.amenities?.length ?? 0) > 0 && (
                        <div>
                          <h4 className="font-medium">All Amenities</h4>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {data.preview.amenities?.map((amenity) => (
                              <Badge key={amenity} variant="outline" className="text-xs">
                                {amenity}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Activities */}
                      {(data.preview.activities?.length ?? 0) > 0 && (
                        <div>
                          <h4 className="font-medium">Suitable Activities</h4>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {data.preview.activities?.map((activity) => (
                              <Badge key={activity} variant="secondary" className="text-xs">
                                {activity}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Image Tags - Phase 24 (FR-104) */}
                      {(data.preview.images?.length ?? 0) > 0 && (
                        <div data-testid="preview-image-tags">
                          <h4 className="font-medium flex items-center gap-2">
                            <Sparkles className="h-4 w-4" />
                            AI Image Tags
                          </h4>
                          <div className="mt-2 grid gap-3">
                            {data.preview.images?.map((image, index) => {
                              const imageTags = getImageTagNames(index);
                              if (imageTags.length === 0) return null;
                              return (
                                <div key={image.url} className="flex items-start gap-3 rounded-md border bg-background p-2">
                                  <img
                                    src={getProxiedImageUrl(image.url, 'thumb')}
                                    alt={image.caption ?? `Image ${String(index + 1)}`}
                                    className="h-16 w-16 rounded object-cover"
                                  />
                                  <div className="flex-1">
                                    <p className="text-xs text-muted-foreground">
                                      Image {String(index + 1)}
                                      {image.caption && `: ${image.caption}`}
                                    </p>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {imageTags.map((tagName) => (
                                        <Badge key={tagName} variant="outline" className="text-xs">
                                          {tagName}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {/* Show message if no images have tags */}
                            {data.preview.images?.every((_, index) => getImageTagNames(index).length === 0) && (
                              <p className="text-xs text-muted-foreground">No image tags have been confirmed yet.</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Pricing details */}
                      <div>
                        <h4 className="font-medium">Pricing Details</h4>
                        <div className="mt-1 space-y-1 text-muted-foreground">
                          <p>Hourly: {data.preview.pricing?.pricePerHour ?? 'Not set'}</p>
                          <p>Daily: {data.preview.pricing?.pricePerDay ?? 'Not set'}</p>
                          <p>Weekly: {data.preview.pricing?.pricePerWeek ?? 'Not set'}</p>
                          <p data-testid="preview-pricing-currency">
                            {data.preview.pricing?.currency
                              ? `Currency: ${data.preview.pricing.currency}`
                              : CURRENCY_NOT_SET_LABEL}
                          </p>
                          {data.preview.minimumBookingHours && (
                            <p>Minimum booking: {data.preview.minimumBookingHours} hours</p>
                          )}
                        </div>
                      </div>

                      {/* External reference */}
                      <div>
                        <h4 className="font-medium">External Reference</h4>
                        <p className="mt-1 font-mono text-xs text-muted-foreground">
                          ID: {data.preview.externalId}
                        </p>
                      </div>
                    </div>
                  </details>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
